import { supabase } from '../lib/supabase';

export const commentService = {
  // Get comments for a specific record
  async getComments(recordType, recordId) {
    try {
      const { data, error } = await supabase?.from('record_comments')?.select(`
          *,
          user_profile:user_profiles!record_comments_user_id_fkey(
            id, full_name, email
          )
        `)?.eq('record_type', recordType)?.eq('record_id', recordId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(comment => ({
        id: comment?.id,
        recordType: comment?.record_type,
        recordId: comment?.record_id,
        userId: comment?.user_id,
        content: comment?.content,
        mentionedUsers: comment?.mentioned_users || [],
        createdAt: comment?.created_at,
        updatedAt: comment?.updated_at,
        userProfile: comment?.user_profile ? {
          id: comment?.user_profile?.id,
          fullName: comment?.user_profile?.full_name,
          email: comment?.user_profile?.email
        } : null
      })) || [];
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  },

  // Create a new comment
  async createComment(commentData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('record_comments')?.insert({
          record_type: commentData?.recordType,
          record_id: commentData?.recordId,
          user_id: user?.id,
          content: commentData?.content,
          mentioned_users: commentData?.mentionedUsers || []
        })?.select(`
          *,
          user_profile:user_profiles!record_comments_user_id_fkey(
            id, full_name, email
          )
        `)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        recordType: data?.record_type,
        recordId: data?.record_id,
        userId: data?.user_id,
        content: data?.content,
        mentionedUsers: data?.mentioned_users || [],
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        userProfile: data?.user_profile ? {
          id: data?.user_profile?.id,
          fullName: data?.user_profile?.full_name,
          email: data?.user_profile?.email
        } : null
      };
    } catch (error) {
      console.error('Error creating comment:', error);
      throw error;
    }
  },

  // Update a comment
  async updateComment(commentId, content) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('record_comments')?.update({ content })?.eq('id', commentId)?.eq('user_id', user?.id)?.select(`
          *,
          user_profile:user_profiles!record_comments_user_id_fkey(
            id, full_name, email
          )
        `)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        recordType: data?.record_type,
        recordId: data?.record_id,
        userId: data?.user_id,
        content: data?.content,
        mentionedUsers: data?.mentioned_users || [],
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        userProfile: data?.user_profile ? {
          id: data?.user_profile?.id,
          fullName: data?.user_profile?.full_name,
          email: data?.user_profile?.email
        } : null
      };
    } catch (error) {
      console.error('Error updating comment:', error);
      throw error;
    }
  },

  // Delete a comment
  async deleteComment(commentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('record_comments')?.delete()?.eq('id', commentId)?.eq('user_id', user?.id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  },

  // Subscribe to real-time comment updates
  subscribeToComments(recordType, recordId, callback) {
    const channel = supabase?.channel(`comments_${recordType}_${recordId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'record_comments',
          filter: `record_type=eq.${recordType},record_id=eq.${recordId}`
        },
        async (payload) => {
          // Fetch the complete comment with user profile
          if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
            const { data } = await supabase?.from('record_comments')?.select(`
                *,
                user_profile:user_profiles!record_comments_user_id_fkey(
                  id, full_name, email
                )
              `)?.eq('id', payload?.new?.id)?.single();

            if (data) {
              callback({
                eventType: payload?.eventType,
                comment: {
                  id: data?.id,
                  recordType: data?.record_type,
                  recordId: data?.record_id,
                  userId: data?.user_id,
                  content: data?.content,
                  mentionedUsers: data?.mentioned_users || [],
                  createdAt: data?.created_at,
                  updatedAt: data?.updated_at,
                  userProfile: data?.user_profile ? {
                    id: data?.user_profile?.id,
                    fullName: data?.user_profile?.full_name,
                    email: data?.user_profile?.email
                  } : null
                }
              });
            }
          } else if (payload?.eventType === 'DELETE') {
            callback({
              eventType: 'DELETE',
              commentId: payload?.old?.id
            });
          }
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }
};